// fieldLabels.js — static label + group map for the All-fields panel
// (tasks #62/#60). Generated from the standard-field-parity and
// custom-field-mirror audits: every SuiteQL column the syncs capture into
// ns_raw gets a human label and a display group. Unknown keys still render
// (prettified) so new capture columns never disappear — they land in
// "Custom fields" (cust* keys) or "Other".
//
// Pure data + tiny pure helpers. No React in this file.

export const GROUP_ORDER = [
  'Identity',
  'Status & dates',
  'Entity',
  'Shipping & tracking',
  'Amounts',
  'Production',
  'Purchasing',
  'Inventory & costing',
  'Tax',
  'Sales & attribution',
  'Accounting',
  'Custom fields',
  'Other',
];

// key → [label, group]. Keys are the lowercase SuiteQL aliases as the syncs
// store them in ns_raw.
const TRANSACTION = {
  id: ['NS internal id', 'Identity'],
  type: ['Transaction type', 'Identity'],
  tranid: ['Transaction #', 'Identity'],
  transactionnumber: ['Transaction number', 'Identity'],
  externalid: ['External id', 'Identity'],
  source: ['Source system', 'Identity'],
  otherrefnum: ['PO / check # (other ref)', 'Identity'],

  trandate: ['Date', 'Status & dates'],
  duedate: ['Due date', 'Status & dates'],
  status: ['Status code', 'Status & dates'],
  approval_status: ['Approval status', 'Status & dates'],
  closedate: ['Close date', 'Status & dates'],
  createddate: ['Created', 'Status & dates'],
  lastmodifieddate: ['Last modified', 'Status & dates'],
  posting: ['Posting', 'Status & dates'],
  posting_period: ['Posting period', 'Status & dates'],
  voided: ['Voided', 'Status & dates'],
  billingstatus: ['Billing status', 'Status & dates'],
  firmed: ['Firmed', 'Status & dates'],
  ordpicked: ['Order picked', 'Status & dates'],

  entity_id: ['Entity (NS id)', 'Entity'],
  entity_name: ['Entity', 'Entity'],
  email: ['Order email', 'Entity'],
  employee: ['Employee / rep (NS id)', 'Entity'],
  employee_name: ['Employee / rep', 'Entity'],

  shipdate: ['Ship date (requested)', 'Shipping & tracking'],
  actualshipdate: ['Actual ship date', 'Shipping & tracking'],
  shipaddress: ['Ship-to address', 'Shipping & tracking'],
  shipmethod: ['Ship method (NS id)', 'Shipping & tracking'],
  shipmethod_name: ['Ship method', 'Shipping & tracking'],
  shipcarrier: ['Ship carrier', 'Shipping & tracking'],
  linkedtrackingnumberlist: ['Tracking numbers', 'Shipping & tracking'],

  foreigntotal: ['Total', 'Amounts'],
  foreignamountpaid: ['Amount paid', 'Amounts'],
  foreignamountunpaid: ['Amount unpaid', 'Amounts'],
  estgrossprofit: ['Est. gross profit', 'Amounts'],
  estgrossprofitpercent: ['Est. gross profit %', 'Amounts'],
  exchangerate: ['Exchange rate', 'Amounts'],
  currency: ['Currency (NS id)', 'Amounts'],

  startdate: ['Planned production start', 'Production'],
  enddate: ['Planned production end', 'Production'],
  actualproductionstartdate: ['Actual production start', 'Production'],
  actualproductionenddate: ['Actual production end', 'Production'],

  terms: ['Terms (NS id)', 'Accounting'],
  terms_name: ['Terms', 'Accounting'],
  subsidiary: ['Subsidiary (NS id)', 'Accounting'],
  subsidiary_name: ['Subsidiary', 'Accounting'],
  memo: ['Memo', 'Accounting'],

  leadsource: ['Lead source (NS id)', 'Sales & attribution'],
  sales_order_type_name: ['Sales order type', 'Sales & attribution'],
  rep_group_name: ['Sales rep group', 'Sales & attribution'],

  custbody7: ['Vendor terms (custbody7)', 'Custom fields'],
  custbodysales_order_type: ['Sales order type (NS id)', 'Custom fields'],
  custbody41: ['Sales rep group (NS id)', 'Custom fields'],
  custbody33: ['Pour completed', 'Production'],
  custbody35: ['Priority (NS id)', 'Production'],
  custbody_wo_date_required: ['Date required by', 'Production'],
};

// Line-level extras (sales_order_lines.ns_raw) reuse the transaction map plus:
const TRANSACTION_LINE = {
  item: ['Item (NS id)', 'Identity'],
  item_name: ['Item', 'Identity'],
  linesequencenumber: ['Line #', 'Identity'],
  quantity: ['Quantity ordered', 'Amounts'],
  quantityshiprecv: ['Quantity shipped/received', 'Amounts'],
  quantitybilled: ['Quantity billed', 'Amounts'],
  quantitybackordered: ['Quantity backordered', 'Amounts'],
  quantitypicked: ['Quantity picked', 'Amounts'],
  quantitypacked: ['Quantity packed', 'Amounts'],
  rate: ['Rate', 'Amounts'],
  amount: ['Amount', 'Amounts'],
  netamount: ['Net amount', 'Amounts'],
  price: ['Price level (NS id)', 'Amounts'],
  price_level_name: ['Price level', 'Amounts'],
  location: ['Location (NS id)', 'Shipping & tracking'],
  location_name: ['Location', 'Shipping & tracking'],
  fulfillable: ['Fulfillable', 'Shipping & tracking'],
  isfullyshipped: ['Fully shipped', 'Shipping & tracking'],
  commitinventory: ['Commit inventory', 'Shipping & tracking'],
  itemfulfillmentchoice: ['Fulfillment choice', 'Shipping & tracking'],
  isclosed: ['Line closed', 'Status & dates'],
  units: ['Units (UOM id)', 'Inventory & costing'],
  kitmemberof: ['Kit member of', 'Identity'],
  assemblycomponent: ['Assembly component', 'Identity'],
  createdfrom: ['Created from (NS id)', 'Identity'],
  createdfrom_name: ['Created from', 'Identity'],
  class: ['Class (NS id)', 'Accounting'],
  class_name: ['Class', 'Accounting'],
  taxline: ['Tax line', 'Tax'],
  custcolitem_ship_date: ['Item ship date', 'Shipping & tracking'],
  line_memo: ['Line memo', 'Accounting'],
};

const ITEM = {
  id: ['NS internal id', 'Identity'],
  itemid: ['Item name/number (SKU)', 'Identity'],
  displayname: ['Display name', 'Identity'],
  fullname: ['Full name', 'Identity'],
  itemtype: ['Item type', 'Identity'],
  isinactive: ['Inactive', 'Status & dates'],
  createddate: ['Created', 'Status & dates'],
  lastmodifieddate: ['Last modified', 'Status & dates'],
  description: ['Sales description', 'Identity'],
  purchasedescription: ['Purchase description', 'Purchasing'],
  storedisplayname: ['Web store display name', 'Identity'],
  storedescription: ['Web store description', 'Identity'],
  stockdescription: ['Stock description', 'Inventory & costing'],
  upccode: ['UPC code', 'Identity'],
  vendorname: ['Vendor name / code', 'Purchasing'],
  cost: ['Purchase price', 'Inventory & costing'],
  averagecost: ['Average cost', 'Inventory & costing'],
  lastpurchaseprice: ['Last purchase price', 'Inventory & costing'],
  totalquantityonhand: ['Total on hand', 'Inventory & costing'],
  totalvalue: ['Total value', 'Inventory & costing'],
  costingmethod: ['Costing method code', 'Inventory & costing'],
  costingmethoddisplay: ['Costing method', 'Inventory & costing'],
  costcategory: ['Cost category (NS id)', 'Inventory & costing'],
  usebins: ['Uses bins', 'Inventory & costing'],
  leadtime: ['Lead time (days)', 'Purchasing'],
  autoleadtime: ['Auto lead time', 'Purchasing'],
  safetystocklevel: ['Safety stock level', 'Inventory & costing'],
  reordermultiple: ['Reorder multiple', 'Purchasing'],
  autoreorderpoint: ['Reorder point (item)', 'Inventory & costing'],
  autopreferredstocklevel: ['Preferred stock level (item)', 'Inventory & costing'],
  supplyreplenishmentmethod: ['Replenishment method', 'Purchasing'],
  seasonaldemand: ['Seasonal demand', 'Purchasing'],
  weight: ['Weight', 'Identity'],
  weightunit: ['Weight unit (NS id)', 'Identity'],
  weightunits: ['Weight units', 'Identity'],
  saleunit: ['Sale unit (NS id)', 'Inventory & costing'],
  purchaseunit: ['Purchase unit (NS id)', 'Inventory & costing'],
  stockunit: ['Stock unit (NS id)', 'Inventory & costing'],
  consumptionunit: ['Consumption unit (NS id)', 'Inventory & costing'],
  unitstype: ['Units type (NS id)', 'Inventory & costing'],
  assetaccount: ['Asset account (NS id)', 'Accounting'],
  expenseaccount: ['COGS/expense account (NS id)', 'Accounting'],
  incomeaccount: ['Income account (NS id)', 'Accounting'],
  intercoexpenseaccount: ['Interco expense account (NS id)', 'Accounting'],
  intercoincomeaccount: ['Interco income account (NS id)', 'Accounting'],
  dropshipexpenseaccount: ['Drop-ship expense account (NS id)', 'Accounting'],
  class: ['Class (NS id)', 'Accounting'],
  subsidiary: ['Subsidiary (NS id)', 'Accounting'],
  isdropshipitem: ['Drop-ship item', 'Purchasing'],
  isspecialorderitem: ['Special order item', 'Purchasing'],
  isphantom: ['Phantom assembly', 'Production'],
  isonline: ['Available online', 'Identity'],
  isfulfillable: ['Fulfillable', 'Shipping & tracking'],
  tracklandedcost: ['Track landed cost', 'Inventory & costing'],
  matchbilltoreceipt: ['Match bill to receipt', 'Purchasing'],
  buildentireassembly: ['Build entire assembly', 'Production'],
  usecomponentyield: ['Use component yield', 'Production'],
  isspecialworkorderitem: ['Special work order item', 'Production'],
  deposit: ['Deposit', 'Amounts'],
  taxschedule: ['Tax schedule (NS id)', 'Tax'],
  taxschedule_name: ['Tax schedule', 'Tax'],
  custitem18: ['Unavailable for work orders', 'Custom fields'],
  custitem6: ['To be sold?', 'Custom fields'],
  custitemlinkedcasesingle: ['Linked case/single (NS id)', 'Custom fields'],
  linked_case_single_name: ['Linked case/single item', 'Custom fields'],
  custitem28: ['Shopify barcode', 'Custom fields'],
  custitem5: ['Brand (NS id)', 'Custom fields'],
  custitem_case_qty: ['Case quantity', 'Custom fields'],
};

const CUSTOMER = {
  id: ['NS internal id', 'Identity'],
  entityid: ['Customer #', 'Identity'],
  companyname: ['Company name', 'Identity'],
  altname: ['Alt name', 'Identity'],
  fullname: ['Full name', 'Identity'],
  firstname: ['First name', 'Identity'],
  lastname: ['Last name', 'Identity'],
  isperson: ['Individual', 'Identity'],
  externalid: ['External id (crosswalk)', 'Identity'],
  stage: ['Stage (customer/prospect/lead)', 'Identity'],
  isinactive: ['Inactive', 'Status & dates'],
  datecreated: ['Created', 'Status & dates'],
  lastmodifieddate: ['Last modified', 'Status & dates'],
  startdate: ['Relationship start', 'Status & dates'],
  firstorderdate: ['First order date', 'Status & dates'],
  lastorderdate: ['Last order date', 'Status & dates'],
  email: ['Email', 'Entity'],
  phone: ['Phone', 'Entity'],
  url: ['Website', 'Entity'],
  comments: ['Comments', 'Entity'],
  category: ['Category (NS id)', 'Entity'],
  entitystatus: ['Entity status (NS id)', 'Entity'],
  territory: ['Territory (NS id)', 'Entity'],
  salesrep: ['Sales rep (NS id)', 'Sales & attribution'],
  salesrep_name: ['Sales rep', 'Sales & attribution'],
  leadsource: ['Lead source (NS id)', 'Sales & attribution'],
  weblead: ['Web lead', 'Sales & attribution'],
  probability: ['Probability', 'Sales & attribution'],
  pricelevel: ['Price level (NS id)', 'Amounts'],
  creditlimit: ['Credit limit', 'Amounts'],
  balancesearch: ['Balance', 'Amounts'],
  depositbalancesearch: ['Deposit balance', 'Amounts'],
  overduebalancesearch: ['Overdue balance', 'Amounts'],
  unbilledorderssearch: ['Unbilled orders', 'Amounts'],
  daysoverduesearch: ['Days overdue', 'Amounts'],
  oncredithold: ['On credit hold', 'Amounts'],
  terms: ['Terms (NS id)', 'Accounting'],
  receivablesaccount: ['Receivables account (NS id)', 'Accounting'],
  currency: ['Currency (NS id)', 'Accounting'],
  taxable: ['Taxable', 'Tax'],
  taxexempt: ['Tax exempt', 'Tax'],
  resalenumber: ['Resale number', 'Tax'],
  shipcomplete: ['Ship complete', 'Shipping & tracking'],
  shippingcarrier: ['Shipping carrier', 'Shipping & tracking'],
  shippingitem: ['Shipping item (NS id)', 'Shipping & tracking'],
  defaultbillingaddress: ['Default billing address (NS id)', 'Shipping & tracking'],
  defaultshippingaddress: ['Default shipping address (NS id)', 'Shipping & tracking'],
  emailpreference: ['Email preference', 'Entity'],
  emailtransactions: ['Email transactions', 'Entity'],
  globalsubscriptionstatus: ['Marketing subscription status', 'Entity'],
  giveaccess: ['Portal access', 'Entity'],
  isjob: ['Is job', 'Identity'],
  custentity18: ['Private label status (NS id)', 'Custom fields'],
  private_label_status_name: ['Private label status', 'Custom fields'],
  custentity17: ['Private label customer', 'Custom fields'],
  custentity13: ['Resale certificate on file', 'Custom fields'],
};

const VENDOR = {
  id: ['NS internal id', 'Identity'],
  entityid: ['Vendor #', 'Identity'],
  companyname: ['Company name', 'Identity'],
  altname: ['Alt name', 'Identity'],
  fullname: ['Full name', 'Identity'],
  legalname: ['Legal name', 'Identity'],
  isperson: ['Individual', 'Identity'],
  isinactive: ['Inactive', 'Status & dates'],
  datecreated: ['Created', 'Status & dates'],
  lastmodifieddate: ['Last modified', 'Status & dates'],
  email: ['Email', 'Entity'],
  phone: ['Phone', 'Entity'],
  fax: ['Fax', 'Entity'],
  url: ['Website', 'Entity'],
  comments: ['Comments', 'Entity'],
  category: ['Category (NS id)', 'Entity'],
  category_name: ['Category', 'Entity'],
  emailpreference: ['Email preference', 'Entity'],
  taxidnum: ['Tax ID (TIN)', 'Tax'],
  is1099eligible: ['1099 eligible', 'Tax'],
  printoncheckas: ['Print on check as', 'Accounting'],
  balance: ['Balance', 'Amounts'],
  balanceprimary: ['Balance (primary)', 'Amounts'],
  prepaymentbalance: ['Prepayment balance', 'Amounts'],
  unbilledorders: ['Unbilled orders', 'Amounts'],
  unbilledordersprimary: ['Unbilled orders (primary)', 'Amounts'],
  terms: ['Terms (NS id)', 'Accounting'],
  terms_name: ['Terms', 'Accounting'],
  currency: ['Currency (NS id)', 'Accounting'],
  payablesaccount: ['Payables account (NS id)', 'Accounting'],
  expenseaccount: ['Default expense account (NS id)', 'Accounting'],
  subsidiary: ['Subsidiary (NS id)', 'Accounting'],
  subsidiary_name: ['Subsidiary', 'Accounting'],
  defaultbillingaddress: ['Default billing address (NS id)', 'Shipping & tracking'],
  defaultshippingaddress: ['Default shipping address (NS id)', 'Shipping & tracking'],
  isjobresourcevend: ['Job resource vendor', 'Identity'],
};

// Work orders are transactions; the Run WO sync's aliases add a few of its own.
const WORK_ORDER = {
  ...TRANSACTION,
  workordernumber: ['Work order #', 'Identity'],
  orderdate: ['Order date', 'Status & dates'],
  priority: ['Priority (NS id)', 'Production'],
  daterequired: ['Date required by', 'Production'],
  pourcompleted: ['Pour completed', 'Production'],
  plannedstartdate: ['Planned production start', 'Production'],
  plannedenddate: ['Planned production end', 'Production'],
  entity: ['Customer (NS id)', 'Entity'],
  customername: ['Customer', 'Entity'],
  customercategory: ['Customer category (NS id)', 'Entity'],
  assemblyitem: ['Assembly item', 'Production'],
  itemdescription: ['Item description', 'Production'],
  fragranceoilid: ['Fragrance oil (NS id)', 'Production'],
  fragranceoilname: ['Fragrance oil', 'Production'],
  vesselid: ['Vessel (NS id)', 'Production'],
  vesselsku: ['Vessel SKU', 'Production'],
  vesselname: ['Vessel', 'Production'],
  cartquantity: ['Cart quantity', 'Production'],
  caseqty: ['Case quantity', 'Production'],
  ozfill: ['Oz fill', 'Production'],
  wick: ['Wick', 'Production'],
  quantity: ['Quantity ordered', 'Production'],
  quantitybuilt: ['Quantity built', 'Production'],
};

export const FIELD_LABELS = {
  transaction: TRANSACTION,
  transaction_line: { ...TRANSACTION, ...TRANSACTION_LINE },
  work_order: WORK_ORDER,
  item: ITEM,
  customer: CUSTOMER,
  vendor: VENDOR,
};

// Keys never worth rendering (REST plumbing, duplicate join keys).
export const HIDDEN_FIELDS = new Set(['links']);

/** Prettify an unmapped SuiteQL key: 'custbody_po_firmed_ship_date' → 'Po firmed ship date'. */
export function prettifyKey(key) {
  const cleaned = String(key)
    .replace(/^cust(body|col|entity|item|record)_?/, '')
    .replace(/_/g, ' ')
    .trim();
  const s = cleaned || String(key);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** True for cust* custom-field keys. */
export function isCustomKey(key) {
  return /^cust(body|col|entity|item|record)/.test(String(key));
}

/**
 * Shape ns_raw into ordered display groups.
 * @param {string} family  key of FIELD_LABELS
 * @param {object} nsRaw   the captured SuiteQL row (may be null)
 * @param {object} overrides  nsRaw key → promoted value (rendered instead of
 *                            the raw value, marked as promoted)
 * @returns {Array<{group: string, fields: Array<{key,label,value,promoted}>}>}
 */
export function groupFields(family, nsRaw, overrides = {}) {
  const labels = FIELD_LABELS[family] || {};
  const byGroup = new Map();
  const push = (group, field) => {
    if (!byGroup.has(group)) byGroup.set(group, []);
    byGroup.get(group).push(field);
  };

  for (const [key, rawValue] of Object.entries(nsRaw || {})) {
    if (HIDDEN_FIELDS.has(key)) continue;
    if (rawValue == null || rawValue === '') continue;
    const known = labels[key];
    const label = known ? known[0] : prettifyKey(key);
    const group = known ? known[1] : (isCustomKey(key) ? 'Custom fields' : 'Other');
    const promoted = Object.prototype.hasOwnProperty.call(overrides, key) && overrides[key] != null;
    push(group, { key, label, value: promoted ? overrides[key] : rawValue, promoted });
  }

  const ordered = [];
  for (const group of GROUP_ORDER) {
    const fields = byGroup.get(group);
    if (fields && fields.length > 0) {
      fields.sort((a, b) => a.label.localeCompare(b.label));
      ordered.push({ group, fields });
    }
  }
  return ordered;
}
